// screens/AccountSpread.tsx — K1: the Account, whole (c2-account-kernel.md).
// Hero = the client party and its phase; body = the dated phase history
// plus the panes K2-K6 fill (Engagements — filled now — Agreements,
// Outcomes, Money, still empty until C4-C6 land); aside = Cross phase, the
// one admin action B9's own gate already enforces.
//
// Storage stays `engagements` (B9, aliased); this screen never says the
// word — every visible string here says "Account" for the relationship,
// "Engagement" only for the K2 bounded engagements in its own pane.
import { useEffect, useState } from 'preact/hooks';
import { ApiError, changeAccountPhase, getAccount, listOutcomes, type AccountPhase, type AccountSummaryApi, type RecordRow } from '../lib/api';
import { BackLink } from '../components/BackLink';
import { OutcomeSheet } from '../components/OutcomeSheet';
import { useRecordsVersion } from '../lib/recordEditor';
import './spread.css';

export interface AccountSpreadProps {
  tenantId: string;
  id: string;
  onBack: () => void;
  onOpenEngagement: (id: string) => void;
  onNewEngagement: () => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; summary: AccountSummaryApi };

const PHASES: { id: AccountPhase; label: string }[] = [
  { id: 'design_partner', label: 'Design partner' },
  { id: 'billable', label: 'Billable' },
  { id: 'closed', label: 'Closed' },
];

function phaseLabel(p: AccountPhase): string {
  return PHASES.find((x) => x.id === p)?.label ?? p;
}

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

const REALISED_CHIP: Record<string, string> = { not_yet: 'not yet', partly: 'partly', yes: 'yes', no: 'no' };

function outcomeRow(o: RecordRow, onOpen: (id: string) => void) {
  const measure = typeof o.fields.measure === 'string' ? o.fields.measure : o.display_name;
  const baseline = o.fields.baseline_value ?? '—';
  const target = o.fields.target_value ?? '—';
  const current = o.fields.current_value ?? '—';
  const realised = REALISED_CHIP[String(o.fields.realised ?? 'not_yet')] ?? 'not yet';
  return (
    <button type="button" class="sp__row" key={o.id} onClick={() => onOpen(o.id)}>
      <span class="sp__row-top">
        <span class="sp__row-name">{measure}</span>
        <span class="sp__row-when">{o.fields.review_on ? `review ${when(o.fields.review_on as string)}` : ''}</span>
      </span>
      <span class="sp__row-sub">
        {String(baseline)} → {String(target)} · now {String(current)} · <span class="sp__chip">{realised}</span>
      </span>
    </button>
  );
}

export function AccountSpread({ tenantId, id, onBack, onOpenEngagement, onNewEngagement }: AccountSpreadProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [crossing, setCrossing] = useState(false);
  const [crossTo, setCrossTo] = useState<AccountPhase>('billable');
  const [crossDate, setCrossDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // A new engagement (K2) is created through the generic record editor
  // overlay, not a form owned by this screen — this is how the Engagements
  // pane sees it without navigating away and back.
  const recordsVersion = useRecordsVersion();

  // K4 — the Outcomes pane. A sheet (OutcomeSheet), not a screen: `sheetFor`
  // holds either 'new' or an existing outcome's id, null when closed.
  const [outcomes, setOutcomes] = useState<RecordRow[]>([]);
  const [outcomesError, setOutcomesError] = useState<string | null>(null);
  const [outcomeSheet, setOutcomeSheet] = useState<'new' | string | null>(null);

  function refreshOutcomes() {
    return listOutcomes(tenantId, { accountId: id })
      .then(setOutcomes)
      .catch((err: unknown) => setOutcomesError(err instanceof ApiError ? err.message : 'outcomes could not be read'));
  }

  function refresh() {
    return getAccount(tenantId, id)
      .then((summary) => setLoad({ phase: 'ready', summary }))
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'this account could not be read',
        });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    void refreshOutcomes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, recordsVersion]);

  async function crossPhase() {
    setNote(null);
    setBusy(true);
    try {
      await changeAccountPhase(tenantId, id, { to: crossTo, effectiveOn: crossDate, reason: reason.trim() || undefined });
      setCrossing(false);
      setReason('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the crossing was refused');
    } finally {
      setBusy(false);
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label="Clients" onClick={onBack} />
        <div class="sp__say">One moment…</div>
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="sp">
        <BackLink label="Clients" onClick={onBack} />
        <div class="sp__say">
          This account could not be read.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { account, party, history, engagements, counts } = load.summary;
  const orderedHistory = [...history].sort((a, b) => b.effectiveOn.localeCompare(a.effectiveOn));

  return (
    <div class="sp">
      <BackLink label="Clients" onClick={onBack} />

      <header class="sp__hero-top">
        <div>
          <div class="sp__label">
            <span class={`sp__chip sp__chip--accent`}>{phaseLabel(account.phase)}</span>
          </div>
          <h1 class="sp__title">{party?.displayName ?? account.name}</h1>
          <p class="sp__sub">since {when(account.startedOn)}</p>
        </div>
      </header>

      <div class="sp__grid">
        <div class="sp__hero">
          <div class="sp__sec"><span>Phase</span></div>
          {orderedHistory.length === 0 ? (
            <div class="sp__empty">No crossing recorded — this account has been {phaseLabel(account.phase).toLowerCase()} since it started.</div>
          ) : (
            orderedHistory.map((h) => (
              <div class="sp__row" key={h.id}>
                <span class="sp__row-top">
                  <span class="sp__row-name">{phaseLabel(h.fromPhase)} → {phaseLabel(h.toPhase)}</span>
                  <span class="sp__row-when">{when(h.effectiveOn)}</span>
                </span>
                {h.reason ? <span class="sp__row-sub">{h.reason}</span> : null}
              </div>
            ))
          )}

          <div class="sp__sec" style={{ marginTop: '16px' }}>
            <span>Engagements</span>
            <span>{counts.engagements}</span>
          </div>
          {engagements.length === 0 ? (
            <div class="sp__empty">No engagements yet. The first one is a design partnership, a build, or a change.</div>
          ) : (
            engagements.map((e) => (
              <button type="button" class="sp__row" key={e.id} onClick={() => onOpenEngagement(e.id)}>
                <span class="sp__row-top">
                  <span class="sp__row-name">
                    <span class="sp__chip">{e.kind.replace(/_/g, ' ')}</span> {e.title}
                  </span>
                  <span class="sp__row-when">{e.sinceDays !== null ? `since ${e.sinceDays}d` : ''}</span>
                </span>
                <span class="sp__row-sub">{e.state ?? '—'}</span>
              </button>
            ))
          )}
          <div class="sp__acts">
            <button type="button" class="sp__act" onClick={onNewEngagement}>New engagement</button>
          </div>

          <div class="sp__sec" style={{ marginTop: '16px' }}><span>Agreements</span></div>
          <div class="sp__empty">No agreements yet.</div>

          <div class="sp__sec" style={{ marginTop: '16px' }}><span>Outcomes</span></div>
          {outcomesError ? (
            <div class="sp__age sp__age--late">{outcomesError}</div>
          ) : outcomes.length === 0 ? (
            <div class="sp__empty">No outcomes yet — every engagement begins with a measurable baseline and target.</div>
          ) : (
            outcomes.map((o) => outcomeRow(o, (oid) => setOutcomeSheet(oid)))
          )}
          <div class="sp__acts">
            <button type="button" class="sp__act" onClick={() => setOutcomeSheet('new')}>New outcome</button>
          </div>

          <div class="sp__sec" style={{ marginTop: '16px' }}><span>Money</span></div>
          <div class="sp__empty">Nothing recorded yet.</div>
        </div>

        <div class="sp__aside">
          <div class="sp__sec"><span>Actions</span></div>
          {!crossing ? (
            <div class="sp__acts">
              <button type="button" class="sp__act" onClick={() => setCrossing(true)}>Cross phase</button>
            </div>
          ) : (
            <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
              <select
                class="sp__act"
                value={crossTo}
                disabled={busy}
                onChange={(e) => setCrossTo((e.target as HTMLSelectElement).value as AccountPhase)}
              >
                {PHASES.map((p) => <option value={p.id} key={p.id}>{p.label}</option>)}
              </select>
              <input
                class="sp__act"
                type="date"
                value={crossDate}
                disabled={busy}
                onInput={(e) => setCrossDate((e.target as HTMLInputElement).value)}
              />
              <input
                class="sp__act"
                type="text"
                placeholder="Reason (optional)"
                value={reason}
                disabled={busy}
                onInput={(e) => setReason((e.target as HTMLInputElement).value)}
              />
              <button type="button" class="sp__act" disabled={busy} onClick={() => void crossPhase()}>Cross</button>
              <button type="button" class="sp__act" disabled={busy} onClick={() => setCrossing(false)}>Never mind</button>
            </div>
          )}
          {note ? <p class="sp__age sp__age--late">{note}</p> : null}
        </div>
      </div>

      {outcomeSheet ? (
        <OutcomeSheet
          tenantId={tenantId}
          accountId={id}
          engagementId={null}
          outcomeId={outcomeSheet === 'new' ? null : outcomeSheet}
          onClose={() => setOutcomeSheet(null)}
          onSaved={() => void refreshOutcomes()}
        />
      ) : null}
    </div>
  );
}
